import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, BadgeCheck, Building2, Coins, Layers, Lock } from 'lucide-react';

/*
 * Homepage bento: RWA, stablecoins, tokenized securities, tokenization platforms and DeFi.
 * Every card is one link to the matching service or industry page. Copy is condensed from
 * those pages (src/data/services.js, src/data/industries.js) — keep them in sync.
 * Visuals are decorative SVG/CSS only (aria-hidden) and cost no JS beyond the hover glow.
 */

const CARDS = [
  {
    key: 'rwa',
    to: '/services/rwa-audit',
    eyebrow: 'Real-world assets',
    title: 'RWA Security',
    body: 'Minting and redemption paths, transfer restrictions, compliance controls and the oracle or attestation layer that links every token to its off-chain asset.',
    chips: ['Mint & redeem', 'Attestation oracles', 'Freeze & seize powers'],
    cta: 'Explore RWA audits',
    accent: '0 169 158',
    span: 'lg:col-span-4',
    Visual: () => <RwaVisual />,
  },
  {
    key: 'stable',
    to: '/services/stablecoin-audit',
    eyebrow: 'Stablecoins',
    title: 'Stablecoin Audits',
    body: 'Peg mechanics, reserve accounting and depeg handling, stress-tested against redemption pressure and adversarial arbitrage.',
    cta: 'Explore stablecoin audits',
    accent: '34 211 238',
    span: 'lg:col-span-2',
    Visual: () => <PegVisual />,
  },
  {
    key: 'securities',
    to: '/services/tokenized-securities-audit',
    eyebrow: 'Security tokens',
    title: 'Tokenized Securities',
    body: 'ERC-1400, ERC-3643 and ERC-1404: transfer restrictions, investor eligibility, cap-table integrity and transfer-agent powers.',
    cta: 'Explore securities audits',
    accent: '129 140 248',
    span: 'lg:col-span-2',
    Visual: () => <ComplianceVisual />,
  },
  {
    key: 'platforms',
    to: '/industries/tokenization-platforms',
    eyebrow: 'Tokenization',
    title: 'Tokenization Platforms',
    body: 'Multi-issuer issuance engines: factory and template flaws, tenant isolation, and the link between token and underlying asset.',
    cta: 'Explore platform security',
    accent: '192 132 252',
    span: 'lg:col-span-2',
    Visual: () => <FactoryVisual />,
  },
  {
    key: 'defi',
    to: '/services/defi-diligence',
    eyebrow: 'DeFi',
    title: 'DeFi Security',
    body: 'Code and economics together: oracles, liquidations, incentive design and composability assumptions under flash-loan conditions.',
    cta: 'Explore DeFi audits',
    accent: '56 189 248',
    span: 'lg:col-span-2',
    Visual: () => <DefiVisual />,
  },
];

/* ── Visuals ─────────────────────────────────────────────────────────────── */

const Node = ({ icon: Icon, label, delay }) => (
  <div className="rwa-node flex flex-col items-center gap-2" style={{ animationDelay: `${delay}s` }}>
    <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/[0.04] ring-1 ring-white/10 sm:h-14 sm:w-14">
      <Icon className="h-5 w-5 text-[color:rgb(var(--acc))] sm:h-6 sm:w-6" strokeWidth={1.75} />
    </div>
    <span className="whitespace-nowrap text-[11px] font-medium text-[#8792a2]">{label}</span>
  </div>
);

const Wire = () => (
  <svg className="h-3 flex-1 -translate-y-3" viewBox="0 0 100 12" preserveAspectRatio="none">
    <line x1="0" y1="6" x2="100" y2="6" stroke="rgba(255,255,255,0.1)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    <line className="rwa-flow" x1="0" y1="6" x2="100" y2="6" stroke="rgb(var(--acc))" strokeWidth="2" strokeDasharray="6 10" vectorEffect="non-scaling-stroke" />
  </svg>
);

const RwaVisual = () => (
  <div className="flex items-center gap-2 px-2 sm:gap-3 sm:px-6">
    <Node icon={Building2} label="Off-chain asset" delay={0} />
    <Wire />
    <Node icon={BadgeCheck} label="Attestation" delay={0.6} />
    <Wire />
    <Node icon={Coins} label="On-chain token" delay={1.2} />
  </div>
);

const PegVisual = () => (
  <svg viewBox="0 0 260 90" className="h-24 w-full">
    <line x1="0" y1="45" x2="260" y2="45" stroke="rgba(255,255,255,0.18)" strokeDasharray="3 5" />
    <text x="256" y="38" textAnchor="end" fontSize="10" fill="#8792a2">$1.00 peg</text>
    <path
      id="peg-path"
      d="M0 47 C 20 40, 35 52, 55 45 S 90 36, 110 46 S 140 70, 160 62 S 190 38, 210 44 S 240 48, 260 45"
      fill="none" stroke="rgb(var(--acc))" strokeWidth="2" strokeLinecap="round"
    />
    <circle r="4" fill="rgb(var(--acc))">
      <animateMotion dur="6s" repeatCount="indefinite" rotate="auto">
        <mpath href="#peg-path" />
      </animateMotion>
    </circle>
  </svg>
);

const ComplianceVisual = () => (
  <div className="space-y-2 px-1">
    {[
      { label: 'Eligible investor', state: 'Allowed', ok: true },
      { label: 'Unverified wallet', state: 'Blocked', ok: false },
      { label: 'Lock-up period', state: 'Held', ok: null },
    ].map((r, i) => (
      <div key={r.label} className="rwa-row flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 ring-1 ring-white/[0.06]" style={{ animationDelay: `${i * 0.5}s` }}>
        <span className="flex items-center gap-2 text-xs text-[#c1c9d2]">
          <Lock className="h-3.5 w-3.5 text-[#8792a2]" strokeWidth={1.75} />
          {r.label}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            r.ok === true ? 'bg-emerald-400/10 text-emerald-300' : r.ok === false ? 'bg-rose-400/10 text-rose-300' : 'bg-amber-400/10 text-amber-300'
          }`}
        >
          {r.state}
        </span>
      </div>
    ))}
  </div>
);

const FactoryVisual = () => (
  <div className="flex flex-col items-center">
    <div className="flex items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2 ring-1 ring-white/10">
      <Layers className="h-4 w-4 text-[color:rgb(var(--acc))]" strokeWidth={1.75} />
      <span className="text-xs font-medium text-[#c1c9d2]">Issuance factory</span>
    </div>
    <svg viewBox="0 0 240 30" className="h-7 w-full max-w-[240px]">
      {[40, 120, 200].map((x, i) => (
        <path key={x} className="rwa-flow" d={`M120 0 C 120 16, ${x} 12, ${x} 30`} fill="none" stroke="rgb(var(--acc))" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="4 6" style={{ animationDelay: `${i * 0.3}s` }} />
      ))}
    </svg>
    <div className="grid w-full max-w-[260px] grid-cols-3 gap-2">
      {['Issuer A', 'Issuer B', 'Issuer C'].map((n) => (
        <div key={n} className="rounded-lg border border-dashed border-white/15 px-1 py-2 text-center text-[11px] text-[#8792a2]">
          {n}
        </div>
      ))}
    </div>
  </div>
);

const DefiVisual = () => (
  <div className="px-1">
    <div className="mb-3 flex items-end justify-between">
      <div>
        <p className="text-[11px] text-[#8792a2]">Health factor</p>
        <p className="text-2xl font-semibold tracking-[-0.02em] text-white tabular-nums">1.08</p>
      </div>
      <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300">Near liquidation</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
      <div className="defi-bar h-full rounded-full bg-gradient-to-r from-rose-400 via-amber-300 to-[color:rgb(var(--acc))]" />
    </div>
    <div className="mt-2 flex justify-between text-[10px] text-[#5b6675]">
      <span>Oracle</span>
      <span>Collateral</span>
      <span>Liquidation</span>
    </div>
  </div>
);

/* ── Card ────────────────────────────────────────────────────────────────── */

const onGlow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
};

const Card = ({ card, index }) => {
  const { Visual } = card;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className={card.span}
    >
      <Link
        to={card.to}
        onMouseMove={onGlow}
        className="rwa-card group relative flex h-full flex-col overflow-hidden rounded-2xl bg-[#0a0f16] p-6 ring-1 ring-white/[0.08] transition-all duration-300 hover:-translate-y-1 hover:ring-[color:rgb(var(--acc)/0.45)] hover:shadow-[0_24px_60px_-24px_rgb(var(--acc)/0.45)] sm:p-7"
        style={{ '--acc': card.accent }}
      >
        {/* cursor-following glow */}
        <span className="rwa-glow pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />

        <div className="relative mb-7 flex min-h-[112px] items-center justify-center rounded-xl bg-[radial-gradient(120%_120%_at_50%_0%,rgb(var(--acc)/0.10),transparent_60%)] py-5 ring-1 ring-white/[0.05]" aria-hidden="true">
          <div className="w-full">
            <Visual />
          </div>
        </div>

        <p className="relative mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-[color:rgb(var(--acc))]">{card.eyebrow}</p>
        <h3 className="relative mb-3 text-xl font-semibold tracking-[-0.02em] text-white sm:text-2xl">{card.title}</h3>
        <p className="relative flex-1 text-[15px] leading-relaxed text-[#a3acb9]">{card.body}</p>

        {card.chips && (
          <ul className="relative mt-5 flex flex-wrap gap-2">
            {card.chips.map((c) => (
              <li key={c} className="rounded-full bg-white/[0.04] px-3 py-1 text-xs text-[#c1c9d2] ring-1 ring-white/10">
                {c}
              </li>
            ))}
          </ul>
        )}

        <span className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e6ebf1] transition-colors group-hover:text-[color:rgb(var(--acc))]">
          {card.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </Link>
    </motion.div>
  );
};

/* ── Section ─────────────────────────────────────────────────────────────── */

const RwaDefiSection = () => (
  <section className="relative overflow-hidden bg-black py-24 md:py-32" aria-labelledby="rwa-defi-heading">
    {/* faint top light */}
    <div className="pointer-events-none absolute left-1/2 top-0 h-px w-[min(900px,90vw)] -translate-x-1/2 bg-gradient-to-r from-transparent via-teal-400/40 to-transparent" aria-hidden="true" />
    <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[min(1100px,100vw)] -translate-x-1/2 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(0,169,158,0.10),transparent_70%)]" aria-hidden="true" />

    <div className="relative mx-auto max-w-6xl px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
      >
        <div className="max-w-2xl">
          <p className="mb-5 inline-flex items-center gap-2.5 text-sm font-semibold text-teal-300">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00A99E] shadow-[0_0_0_4px_rgba(0,169,158,0.18)]" aria-hidden="true" />
            Real-world assets &amp; DeFi
          </p>
          <h2 id="rwa-defi-heading" className="text-4xl font-semibold leading-[1.08] tracking-[-0.03em] text-white md:text-5xl">
            Security for tokenized value,{' '}
            <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-indigo-300 bg-clip-text text-transparent">
              from mint to market.
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-[#a3acb9]">
            Specialist audits for the protocols that bring real-world assets, stablecoins and securities on-chain, and the DeFi markets they trade in.
          </p>
        </div>
        <Link
          to="/services"
          className="group inline-flex shrink-0 items-center gap-1.5 self-start rounded-full px-5 py-2.5 text-sm font-semibold text-[#e6ebf1] ring-1 ring-white/15 transition-colors hover:bg-white/[0.06] hover:ring-white/30 md:self-auto"
        >
          All services
          <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
        {CARDS.map((card, i) => (
          <Card key={card.key} card={card} index={i} />
        ))}
      </div>
    </div>

    <style>{`
      .rwa-glow {
        background: radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), rgb(var(--acc) / 0.12), transparent 45%);
      }
      .rwa-flow { animation: rwaFlow 1.6s linear infinite; }
      @keyframes rwaFlow { to { stroke-dashoffset: -32; } }
      .rwa-node { animation: rwaPulse 3.6s ease-in-out infinite; }
      @keyframes rwaPulse { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
      .rwa-row { animation: rwaRow 4.5s ease-in-out infinite; }
      @keyframes rwaRow { 0%, 100% { opacity: 0.75; } 40% { opacity: 1; } }
      .defi-bar { width: 38%; animation: defiBar 5s ease-in-out infinite alternate; }
      @keyframes defiBar { from { width: 34%; } to { width: 62%; } }
      @media (prefers-reduced-motion: reduce) {
        .rwa-flow, .rwa-node, .rwa-row, .defi-bar { animation: none; }
      }
    `}</style>
  </section>
);

export default RwaDefiSection;
